import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { IPdfTemplate } from '../models/pdf-template.interface';

export abstract class BasePdfTemplate implements IPdfTemplate {
  abstract templateName: string;
  abstract templateVersion: string;
  abstract description: string;
  abstract pageSize: 'A3' | 'A4' | 'A5' | 'LETTER' | 'THERMAL';
  previewImage?: string;

  // Common helper methods that all templates can use
  protected formatCurrency(amount: number): string {
    return `₹${amount.toFixed(2)}`;
  }

  protected formatCurrencyWhole(amount: number): string {
    return `₹${Math.round(amount)}`;
  }

  protected formatDate(date: Date | string): string {
    if (typeof date === 'string') {
      return date;
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  protected formatDateTime(date: Date | string): string {
    if (typeof date === 'string') {
      return date;
    }

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  protected getCustomerAddress(data: InvoiceData): string {
    const addr = data.customerAddress;
    if (!addr) return '';
    if (typeof addr === 'string') return addr;

    const parts = [
      addr.line1,
      addr.line2,
      addr.line3,
      addr.city,
      addr.state,
      addr.country,
      addr.zipCode,
    ].filter(Boolean);

    return parts.join(', ');
  }

  protected getCustomerAddressLines(data: InvoiceData): string[] {
    const addr = data.customerAddress;
    if (!addr) return [];

    return [
      addr.line1,
      addr.line2,
      addr.line3,
      [addr.city, addr.state].filter(Boolean).join(', '),
      [addr.country, addr.zipCode].filter(Boolean).join(' - '),
    ].filter(Boolean) as string[];
  }

  protected getPaymentMethodText(data: InvoiceData): string {
    const paymentMethods: string[] = [];

    // Check each payment method and add to array if amount > 0
    if (data.cashAmount && data.cashAmount > 0) {
      paymentMethods.push('CASH');
    }
    if (data.cardAmount && data.cardAmount > 0) {
      paymentMethods.push('CARD');
    }
    if (data.upiAmount && data.upiAmount > 0) {
      paymentMethods.push('UPI');
    }

    // Return comma-separated list or fallback to 'CASH'
    return paymentMethods.length > 0 ? paymentMethods.join(',') : 'CASH';
  }

  // Abstract method that each template must implement
  abstract generatePdfDefinition(data: InvoiceData): TDocumentDefinitions;

  // Page size configurations
  protected getPageSize(): any {
    switch (this.pageSize) {
      case 'A3':
        return 'A3';
      case 'A4':
        return 'A4';
      case 'A5':
        return 'A5';
      case 'LETTER':
        return 'LETTER';
      case 'THERMAL':
        return { width: 226.77, height: 842 };
      default:
        return 'A4';
    }
  }

  protected getPageMargins(): [number, number, number, number] {
    switch (this.pageSize) {
      case 'A3':
        return [40, 40, 40, 40]; // 297mm×420mm (or 420mm×297mm) - A3
      case 'A4':
        return [30, 30, 30, 30]; // 210mm×297mm (or 297mm×210mm) - A4
      case 'A5':
        return [20, 20, 20, 20]; // 148mm×210mm (or 210mm×148mm) - A5
      case 'THERMAL':
        return [10, 10, 10, 10];
      default:
        return [10, 10, 10, 10]; // Thermal
    }
  }
}
